import React, { useState } from "react";

export default function BonusCalculator({
  depositBonusPer = 30,
  maxBonus = 2100,
  lotsPer100 = 2.5,
}) {
  const [depositAmount, setDepositAmount] = useState(1000);

  const numAmount = Math.max(0, Number(depositAmount) || 0);

  // Dynamic % bonus capped at maxBonus
  const rawBonus = numAmount * (Number(depositBonusPer) / 100);
  const calculatedBonus = Math.min(Number(maxBonus), Math.round(rawBonus));

  // Lots required: $100 = lotsPer100; odd amounts round up to nearest $100 unit
  const units = numAmount > 0 ? Math.ceil(numAmount / 100) : 0;
  const lotsRequired = (units * Number(lotsPer100))
    .toFixed(units % 2 === 0 ? 1 : 2)
    .replace(/\.00$/, "")
    .replace(/\.0$/, "");

  // Progress towards max bonus
  const progressPercent =
    Number(maxBonus) > 0
      ? Math.min(100, Math.round((calculatedBonus / Number(maxBonus)) * 100))
      : 0;

  const handleAmountChange = (e) => {
    const val = e.target.value.replace(/[^0-9]/g, "");
    setDepositAmount(val === "" ? "" : Number(val));
  };

  const presetAmounts = [100, 500, 1000, 1330, 2500, 5000, 7000];

  return (
    <div
      className="card border h-100 shadow-sm"
      style={{
        borderRadius: "var(--bs-border-radius-xl)",
        backgroundColor: "var(--bs-card-bg)",
        borderColor: "var(--bs-border-color)",
      }}
    >
      <div className="card-body p-4 d-flex flex-column justify-content-between">
        <div>
          {/* Header */}
          <div className="mb-3">
            <h5
              className="fw-bold mb-1"
              style={{ color: "var(--bs-emphasis-color)", fontSize: "1.1rem" }}
            >
              Bonus Calculator
            </h5>
            <p className="mb-0 font-size-13" style={{ color: "var(--bs-secondary-color)" }}>
              Enter your deposit amount (USD)
            </p>
          </div>

          {/* Deposit Input Field */}
          <div className="mb-3">
            <div
              className="d-flex align-items-center px-3 py-2 rounded-3 border"
              style={{
                backgroundColor: "var(--bs-tertiary-bg)",
                borderColor: "var(--bs-border-color-translucent)",
                transition: "all 0.2s ease",
              }}
            >
              <span
                className="fw-bold me-2 font-size-18"
                style={{ color: "var(--bs-primary)" }}
              >
                $
              </span>
              <input
                type="text"
                className="form-control border-0 p-0 shadow-none fw-bold"
                style={{
                  backgroundColor: "transparent",
                  fontSize: "1.25rem",
                  color: "var(--bs-emphasis-color)",
                }}
                value={depositAmount === "" ? "" : depositAmount}
                onChange={handleAmountChange}
                placeholder="0"
              />
              {numAmount > 0 && (
                <button
                  type="button"
                  className="btn btn-sm btn-link p-0 text-decoration-none"
                  style={{ color: "var(--bs-secondary-color)" }}
                  onClick={() => setDepositAmount(0)}
                  title="Clear"
                >
                  <i className="bx bx-x font-size-18"></i>
                </button>
              )}
            </div>

            {/* Quick preset chips */}
            <div
              className="d-flex flex-nowrap align-items-center gap-1.5 mt-2 overflow-x-auto pb-1"
              style={{
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              {presetAmounts.map((amt) => {
                const isSelected = numAmount === amt;
                return (
                  <button
                    key={amt}
                    type="button"
                    onClick={() => setDepositAmount(amt)}
                    className="btn btn-sm px-2 py-1 text-nowrap flex-shrink-0 flex-grow-1"
                    style={{
                      fontSize: "11px",
                      fontWeight: 600,
                      borderRadius: "var(--bs-border-radius-sm)",
                      backgroundColor: isSelected
                        ? "var(--bs-primary)"
                        : "var(--bs-tertiary-bg)",
                      color: isSelected
                        ? "var(--bs-white)"
                        : "var(--bs-body-color)",
                      border: isSelected
                        ? "none"
                        : "1px solid var(--bs-border-color)",
                      transition: "all 0.15s ease",
                    }}
                  >
                    ${amt.toLocaleString()}
                  </button>
                );
              })}
            </div>
          </div>

          {/* 3 Metric Summary Boxes */}
          <div className="row g-2 mb-3">
            {/* Box 1: Your Bonus */}
            <div className="col-4">
              <div
                className="p-3 text-center h-100 rounded-3"
                style={{
                  backgroundColor: "var(--bs-primary-bg-subtle)",
                  border: "1px solid var(--bs-primary-border-subtle)",
                }}
              >
                <div
                  className="text-uppercase fw-semibold mb-1"
                  style={{
                    fontSize: "10px",
                    letterSpacing: "0.5px",
                    color: "var(--bs-secondary-color)",
                  }}
                >
                  YOUR BONUS
                </div>
                <div
                  className="fw-bold font-size-18"
                  style={{ color: "var(--bs-primary)" }}
                >
                  ${calculatedBonus.toLocaleString()}
                </div>
              </div>
            </div>

            {/* Box 2: Max Bonus */}
            <div className="col-4">
              <div
                className="p-3 text-center h-100 rounded-3"
                style={{
                  backgroundColor: "var(--bs-tertiary-bg)",
                  border: "1px solid var(--bs-border-color)",
                }}
              >
                <div
                  className="text-uppercase fw-semibold mb-1"
                  style={{
                    fontSize: "10px",
                    letterSpacing: "0.5px",
                    color: "var(--bs-secondary-color)",
                  }}
                >
                  MAX BONUS
                </div>
                <div
                  className="fw-bold font-size-18"
                  style={{ color: "var(--bs-primary)" }}
                >
                  ${maxBonus.toLocaleString()}
                </div>
              </div>
            </div>

            {/* Box 3: Lots to Unlock */}
            <div className="col-4">
              <div
                className="p-3 text-center h-100 rounded-3"
                style={{
                  backgroundColor: "var(--bs-tertiary-bg)",
                  border: "1px solid var(--bs-border-color)",
                }}
              >
                <div
                  className="text-uppercase fw-semibold mb-1"
                  style={{
                    fontSize: "10px",
                    letterSpacing: "0.5px",
                    color: "var(--bs-secondary-color)",
                  }}
                >
                  LOTS TO UNLOCK
                </div>
                <div
                  className="fw-bold font-size-18"
                  style={{ color: "var(--bs-primary)" }}
                >
                  {lotsRequired}L
                </div>
              </div>
            </div>
          </div>

          {/* Progress Bar towards $2,100 limit */}
          <div className="mb-2">
            <div
              className="progress"
              style={{
                height: "6px",
                backgroundColor: "var(--bs-border-color)",
                borderRadius: "3px",
              }}
            >
              <div
                className="progress-bar"
                role="progressbar"
                style={{
                  width: `${progressPercent}%`,
                  backgroundColor: "var(--bs-primary)",
                  borderRadius: "3px",
                  transition: "width 0.3s ease",
                }}
              />
            </div>
          </div>
        </div>

        {/* Footnotes matching the design */}
        <div
          className="pt-2 border-top"
          style={{ borderColor: "var(--bs-border-color)" }}
        >
          <div className="d-flex align-items-center justify-content-between font-size-11 mb-1">
            <span style={{ color: "var(--bs-secondary-color)" }}>
              {numAmount === 0
                ? "Enter amount above"
                : `Depositing $${numAmount.toLocaleString()} qualifies for ${depositBonusPer}%`}
            </span>
            <span className="fw-semibold" style={{ color: "var(--bs-primary)" }}>
              {progressPercent}% of Max
            </span>
          </div>
          <div className="font-size-11" style={{ color: "var(--bs-secondary-color)" }}>
            Every $100 deposited = {lotsPer100} standard lots to unlock your {depositBonusPer}% bonus.
          </div>
        </div>
      </div>
    </div>
  );
}
